import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  getKeycloak,
  initializeKeycloak,
  extractUserInfo,
  login as keycloakLogin,
  logout as keycloakLogout,
  isAuthDisabled,
  KeycloakUserInfo,
} from '../services/keycloak';
import { ShieldCheck, Lock, AlertTriangle, RefreshCw } from 'lucide-react';
import { LoginScreen } from '../components/LoginScreen';

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: KeycloakUserInfo | null;
  token: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  authDisabled: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<KeycloakUserInfo | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const initAuth = async () => {
    setIsLoading(true);
    setError(null);

    if (isAuthDisabled) {
      setIsAuthenticated(true);
      setUser(extractUserInfo());
      setToken('offline-token');
      setIsLoading(false);
      return;
    }

    try {
      const authenticated = await initializeKeycloak();
      console.log('%c[AuthContext] Init resolved, authenticated:', 'color: #10b981; font-weight: bold;', authenticated);
      setIsAuthenticated(authenticated);
      if (authenticated) {
        const userInfo = extractUserInfo();
        console.log('%c[AuthContext] Extracted user profile:', 'color: #10b981; font-weight: bold;', userInfo);
        setUser(userInfo);
        setToken(getKeycloak().token || null);
      }
    } catch (err: any) {
      console.error('%c[AuthContext] Auth initialization failed:', 'color: #ef4444; font-weight: bold;', err);
      setError(
        err?.message ||
          'Failed to connect to Keycloak SSO at auth.dhristhi.app. Check network connection or Keycloak CORS/Web Origins settings.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initAuth();
  }, []);

  const handleLogin = async () => {
    try {
      await keycloakLogin();
    } catch (err: any) {
      console.error('[AuthContext] Login error:', err);
      setError(err?.message || 'Failed to redirect to login');
    }
  };

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      await keycloakLogout();
    } catch (err: any) {
      console.error('[AuthContext] Logout error:', err);
      setIsLoading(false);
    }
  };

  // 1. Loading Splash Screen during SSO check
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-slate-900 text-slate-100 p-6">
        <div className="max-w-md w-full text-center space-y-6 animate-in fade-in duration-300">
          <div className="relative inline-flex items-center justify-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-accent-dark to-accent flex items-center justify-center text-white shadow-2xl shadow-accent/40 animate-pulse">
              <ShieldCheck className="w-10 h-10" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-slate-800 border-2 border-slate-900 flex items-center justify-center text-accent">
              <Lock className="w-3.5 h-3.5" />
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-black tracking-tight text-white">
              DPDP Obligations Register
            </h1>
            <p className="text-sm text-slate-400 font-medium">
              Checking session with Dhristhi Auth (<span className="text-accent">dpdp-dhristhi</span>)...
            </p>
          </div>

          <div className="flex justify-center items-center gap-2 text-xs text-slate-500 font-mono">
            <RefreshCw className="w-3.5 h-3.5 animate-spin text-accent" />
            <span>Verifying OpenID Connect Session</span>
          </div>
        </div>
      </div>
    );
  }

  // 2. Error State (e.g., CORS / Network error)
  if (error && !isAuthenticated) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-slate-950 text-slate-100 p-6">
        <div className="max-w-lg w-full bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 mx-auto">
            <AlertTriangle className="w-8 h-8" />
          </div>

          <div className="space-y-2">
            <h2 className="text-xl font-bold text-white">Authentication Service Unavailable</h2>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed text-left bg-slate-950 p-4 rounded-xl border border-slate-800 font-mono break-words">
              {error}
            </p>
          </div>

          <div className="text-xs text-slate-400 text-left space-y-2 bg-slate-800/50 p-4 rounded-xl">
            <p className="font-semibold text-slate-200">Keycloak Admin Verification Checklist:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Keycloak realm <code className="text-accent">dpdp-dhristhi</code> exists on <code className="text-accent">auth.dhristhi.app</code>.</li>
              <li>Client <code className="text-accent">dpdp-register</code> is created as <strong>Public</strong> (Client authentication: OFF).</li>
              <li><strong>Valid Redirect URIs</strong> &amp; <strong>Web Origins</strong> include <code className="text-slate-200">http://127.0.0.1:5173</code> &amp; <code className="text-slate-200">+</code> (strictly without trailing slashes).</li>
            </ul>
          </div>

          <div className="pt-2">
            <button
              onClick={() => initAuth()}
              className="w-full py-2.5 px-4 rounded-xl font-semibold text-xs text-white bg-accent hover:bg-accent-dark transition-all flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-accent/20"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Retry SSO Connection</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 3. Unauthenticated / Signed-Out Landing Screen (Strict Keycloak SSO Login with Dhristhi Branding)
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  // 4. Authenticated state
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        token,
        login: handleLogin,
        logout: handleLogout,
        authDisabled: isAuthDisabled,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
